import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import { BarChart } from "@mui/x-charts/BarChart";
import { Alert, Button, Card, Paper, Skeleton, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";
import ForecastDropdown from "./ForecastDropdown";
import ForecastPeriodDropdown from "./ForecastPeriodDropdown";
import axiosInstance from "../AxiosInstance";
import { generateMonthLabels } from "../Utilities/monthLabelUtils";
import CustomerOrderingDashboardPanel from "./CustomerOrderingDashboardPanel";
import {
    dashboardWidgetCardSx,
    formatCompactGbp,
    formatGbp,
    formatSignedPercent,
    formatUnits,
    getDashboardChartColors,
    getSeriesColor,
    formatYtdAttainment,
} from "./dashboard/dashboardDisplay";
import { KpiStatCard, YtdPieWidget } from "./dashboard/DashboardWidgets";

interface Forecast {
    forecastId: number;
    name: string;
    startPeriod: number;
    startDate: string;
    forecastPeriods: ForecastPeriod[];
}

interface ForecastPeriod {
    forecastPeriodId: number;
    forecastId: number;
    name: string;
    isLocked: boolean;
    periodIndex: number;
    hasForcastWeekElapsed: boolean;
}

interface ChartSeries {
    label: string;
    data: number[];
}

interface TopCustomerData {
    customerId: number;
    customerName: string;
    value: number;
    units: number;
}

interface TopSkuData {
    productId: number;
    skuDescription: string;
    value: number;
    units: number;
}

interface DashboardChartData {
    startMonth: number;
    fiscalCalendarRuleSet?: number;
    mainChart: ChartSeries[];
    topCustomers: TopCustomerData[];
    topSkus: TopSkuData[];
}

interface DatasetItem {
    label: string;
    [key: string]: number | string;
}

const Home: React.FC = () => {
    const theme = useTheme();
    const [mfaGraceBanner, setMfaGraceBanner] = useState<{ until: string } | null>(null);
    const [loadingForecasts, setLoadingForecasts] = useState<boolean>(true);
    const [forecasts, setForecasts] = useState<Forecast[]>([]);
    const [selectedForecastId, setSelectedForecastId] = useState<number | null>(null);
    const [selectedForecastPeriodId, setSelectedForecastPeriodId] = useState<number | null>(null);
    const [mainChartData, setMainChartData] = useState<ChartSeries[]>([]);
    const [dataset, setDataset] = useState<DatasetItem[]>([]);
    const [loadingChart, setLoadingChart] = useState<boolean>(false);
    const [topCustomersData, setTopCustomersData] = useState<TopCustomerData[]>([]);
    const [topSkusData, setTopSkusData] = useState<TopSkuData[]>([]);
    const [fiscalStartMonth, setFiscalStartMonth] = useState<number>(1);
    const [dashboardError, setDashboardError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;

        const loadMfaStatus = async (): Promise<void> => {
            try {
                const { data } = await axiosInstance.get<{
                    enabled: boolean;
                    graceEndsAtUtc?: string | null;
                }>("/mfa/status");
                if (cancelled) {
                    return;
                }
                if (!data.enabled && data.graceEndsAtUtc) {
                    const end = new Date(data.graceEndsAtUtc).getTime();
                    if (end > Date.now()) {
                        setMfaGraceBanner({ until: new Date(data.graceEndsAtUtc).toLocaleString() });
                        return;
                    }
                }
                setMfaGraceBanner(null);
            } catch {
                if (!cancelled) {
                    setMfaGraceBanner(null);
                }
            }
        };

        void loadMfaStatus();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const storedForecast = JSON.parse(localStorage.getItem("selectedForecast") || "{}");
        const storedForecastPeriodId = localStorage.getItem("selectedForecastPeriodId");

        if (storedForecast?.forecastId) {
            setSelectedForecastId(storedForecast.forecastId);
        }

        if (storedForecastPeriodId && storedForecastPeriodId !== "null") {
            setSelectedForecastPeriodId(parseInt(storedForecastPeriodId, 10));
        } else {
            setSelectedForecastPeriodId(null);
        }

        fetchForecastsAndPeriods();
    }, []);

    const fetchForecastsAndPeriods = async (): Promise<void> => {
        try {
            setLoadingForecasts(true);
            const response = await axiosInstance.get<Forecast[]>("/forecasts");
            setForecasts(response.data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingForecasts(false);
        }
    };

    const handleForecastSelected = (id: number): void => {
        localStorage.setItem("selectedForecast", JSON.stringify({ forecastId: id }));
        setSelectedForecastId(id);
        setSelectedForecastPeriodId(null);
    };

    const handleForecastPeriodSelected = (forecastPeriodId: number | null): void => {
        if (forecastPeriodId) {
            localStorage.setItem("selectedForecastPeriodId", forecastPeriodId.toString());
            setSelectedForecastPeriodId(forecastPeriodId);
        } else {
            localStorage.removeItem("selectedForecastPeriodId");
            setSelectedForecastPeriodId(null);
        }
    };

    const fetchDashboardData = async (): Promise<void> => {
        if (!selectedForecastId || !selectedForecastPeriodId) return;

        try {
            setLoadingChart(true);
            setDashboardError(null);
            const response = await axiosInstance.get<DashboardChartData>("/dashboard", {
                params: {
                    forecastId: selectedForecastId,
                    forecastPeriodId: selectedForecastPeriodId,
                },
            });

            const data = response.data;
            const startMonth = data.startMonth;

            const months = generateMonthLabels(startMonth, data.fiscalCalendarRuleSet);

            const newDataset: DatasetItem[] = months.map((month, index) => ({
                label: month,
                ...data.mainChart?.reduce((acc: Record<string, number>, series: ChartSeries) => {
                    acc[series.label] = series.data[index] || 0;
                    return acc;
                }, {} as Record<string, number>),
            }));

            setDataset(newDataset);
            setFiscalStartMonth(startMonth || 1);
            setMainChartData(data.mainChart || []);
            setTopCustomersData(data.topCustomers || []);
            setTopSkusData(data.topSkus || []);
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
            setDashboardError("Could not load dashboard data.");
        } finally {
            setLoadingChart(false);
        }
    };

    useEffect(() => {
        if (selectedForecastId && selectedForecastPeriodId) {
            fetchDashboardData();
        }
    }, [selectedForecastId, selectedForecastPeriodId]);

    const selectedForecast = forecasts.find((f) => f.forecastId === selectedForecastId);
    const chartColors = getDashboardChartColors(theme.palette.mode);
    const hasDashboardSelection = Boolean(selectedForecastId && selectedForecastPeriodId);

    const getSeriesByLabel = (label: string): number[] => {
        return mainChartData.find((series) => series.label === label)?.data || [];
    };

    const nonActualSeries = mainChartData.filter((series) => series.label !== "Actuals");
    const budgetSeries = nonActualSeries[0];
    const forecastSeries = nonActualSeries[1] || nonActualSeries[0];

    const budgetData = budgetSeries?.data || [];
    const forecastData = forecastSeries?.data || [];
    const actualsData = getSeriesByLabel("Actuals");

    const fullYearBudget = budgetData.reduce((sum, value) => sum + value, 0);
    const fullYearForecast = forecastData.reduce((sum, value) => sum + value, 0);

    const now = new Date();
    const fiscalCurrentMonthIndex = ((now.getMonth() + 1) - fiscalStartMonth + 12) % 12;
    const ytdMonthCount = Math.min(fiscalCurrentMonthIndex + 1, Math.max(actualsData.length, forecastData.length, 12));

    const ytdActuals = actualsData.slice(0, ytdMonthCount).reduce((sum, value) => sum + value, 0);
    const ytdBudget = budgetData.slice(0, ytdMonthCount).reduce((sum, value) => sum + value, 0);
    const ytdAttainment = formatYtdAttainment(ytdActuals, ytdBudget);

    const fullYearVariance = fullYearForecast - fullYearBudget;
    const fullYearVariancePct = fullYearBudget === 0 ? null : (fullYearVariance / fullYearBudget) * 100;
    const monthlyVariance = forecastData.map((value, index) => value - (budgetData[index] || 0));
    const ytdActualsSparkline = actualsData.slice(0, ytdMonthCount);
    const ytdBudgetSparkline = budgetData.slice(0, ytdMonthCount);

    return (
        <Box className="component-container">
            {mfaGraceBanner ? (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" component={RouterLink} to="/settings/profile">
                            Set up MFA
                        </Button>
                    }
                >
                    Secure your account: enable two-factor authentication before {mfaGraceBanner.until}.
                </Alert>
            ) : null}

            <Paper elevation={2} sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5, p: 3, flexWrap: "wrap" }}>
                {loadingForecasts ? (
                    <Skeleton variant="rectangular" width={220} height={56} sx={{ borderRadius: 1 }} />
                ) : (
                    <ForecastDropdown
                        forecasts={forecasts}
                        selectedForecastId={selectedForecastId}
                        onForecastSelected={handleForecastSelected}
                    />
                )}
                {loadingForecasts ? (
                    <Skeleton variant="rectangular" width={220} height={56} sx={{ borderRadius: 1 }} />
                ) : (
                    <ForecastPeriodDropdown
                        forecastPeriods={selectedForecast?.forecastPeriods || []}
                        selectedForecastPeriodId={selectedForecastPeriodId}
                        onForecastPeriodSelected={handleForecastPeriodSelected}
                    />
                )}
            </Paper>

            {hasDashboardSelection && dashboardError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {dashboardError}
                </Alert>
            ) : null}

            {hasDashboardSelection && !dashboardError && (
                <>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                            <KpiStatCard
                                label="YTD Actual Revenue"
                                loading={loadingChart}
                                value={formatGbp(ytdActuals)}
                                caption={`First ${ytdMonthCount} periods`}
                                sparkline={ytdActualsSparkline}
                                sparklineColor={chartColors.sparklineActuals}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                            <KpiStatCard
                                label="Actual vs Budget"
                                loading={loadingChart}
                                value={ytdAttainment.value}
                                caption={ytdAttainment.caption}
                                sparkline={ytdBudgetSparkline}
                                sparklineColor={chartColors.budget}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                            <KpiStatCard
                                label="Full-year Forecast"
                                loading={loadingChart}
                                value={formatGbp(fullYearForecast)}
                                caption={`Budget ${formatGbp(fullYearBudget)}`}
                                sparkline={forecastData}
                                sparklineColor={chartColors.sparklineForecast}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                            <KpiStatCard
                                label="Forecast vs Budget"
                                loading={loadingChart}
                                value={formatGbp(fullYearVariance)}
                                caption={
                                    fullYearVariancePct === null
                                        ? "No budget to compare"
                                        : formatSignedPercent(fullYearVariancePct)
                                }
                                captionColor={
                                    fullYearVariancePct === null
                                        ? "text.secondary"
                                        : fullYearVariance >= 0
                                            ? "success.main"
                                            : "error.main"
                                }
                                sparkline={monthlyVariance}
                                sparklineColor={chartColors.sparklineVariance}
                            />
                        </Grid>
                    </Grid>

                    <Card variant="outlined" sx={{ ...dashboardWidgetCardSx, mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, letterSpacing: "-0.01em" }}>
                            Gross Revenue
                        </Typography>
                        {loadingChart ? (
                            <Skeleton variant="rectangular" width="100%" height={340} sx={{ mt: 2, borderRadius: 1 }} />
                        ) : (
                            <BarChart
                                height={340}
                                borderRadius={6}
                                dataset={dataset}
                                grid={{ horizontal: true }}
                                xAxis={[
                                    {
                                        dataKey: "label",
                                        scaleType: "band",
                                        tickLabelStyle: { fontSize: 11 },
                                    },
                                ]}
                                yAxis={[
                                    {
                                        valueFormatter: (value) => formatCompactGbp(Number(value ?? 0)),
                                        tickLabelStyle: { fontSize: 11 },
                                    },
                                ]}
                                series={mainChartData.map((series) => ({
                                    dataKey: series.label,
                                    label: series.label,
                                    color: getSeriesColor(series.label, chartColors),
                                    valueFormatter: (value) => formatGbp(Number(value ?? 0)),
                                }))}
                                margin={{ left: 8, right: 8, top: 24, bottom: 4 }}
                                slotProps={{
                                    legend: {
                                        direction: "row",
                                        position: { vertical: "top", horizontal: "right" },
                                        padding: 0,
                                    },
                                }}
                            />
                        )}
                    </Card>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 12, xl: 6 }}>
                            <YtdPieWidget
                                title="YTD Top Customers"
                                loading={loadingChart}
                                items={topCustomersData.map((customer) => ({
                                    id: customer.customerId,
                                    label: customer.customerName,
                                    value: customer.value,
                                    units: customer.units,
                                }))}
                                emptyMessage="No customer data available"
                                colors={chartColors.pie}
                                formatCurrency={formatGbp}
                                formatUnits={formatUnits}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, xl: 6 }}>
                            <YtdPieWidget
                                title="YTD Top Products"
                                loading={loadingChart}
                                items={topSkusData.map((sku) => ({
                                    id: sku.productId,
                                    label: sku.skuDescription,
                                    value: sku.value,
                                    units: sku.units,
                                }))}
                                emptyMessage="No SKU data available"
                                colors={chartColors.pie}
                                formatCurrency={formatGbp}
                                formatUnits={formatUnits}
                            />
                        </Grid>
                    </Grid>
                </>
            )}

            <CustomerOrderingDashboardPanel />
        </Box>
    );
};

export default Home;
